import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  AccountRef,
  AppUser,
  AuditRecord,
  CommitPlanInput,
  CommitResult,
  CreateQuoteInput,
  IncomeAccount,
  ItemInput,
  MappedEntity,
  MeResponse,
  QboStatus,
  Quote,
  RefItem,
  ResolveResult,
  Role,
} from './types';

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/auth/me'), staleTime: 60_000 });
}

export function useQboStatus() {
  return useQuery({
    queryKey: ['qbo-status'],
    queryFn: () => api.get<QboStatus>('/qbo/status'),
    staleTime: 30_000,
  });
}

export function useQuotes() {
  return useQuery({ queryKey: ['quotes'], queryFn: () => api.get<Quote[]>('/api/quotes') });
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.get<Quote>(`/api/quotes/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQuoteInput) => api.post<Quote>('/api/quotes', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useRefreshQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Quote>(`/api/quotes/${id}/refresh`),
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.setQueryData(['quote', q.id], q);
    },
  });
}

export function useSubmitQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Quote>(`/api/quotes/${id}/submit`),
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.setQueryData(['quote', q.id], q);
    },
  });
}

export interface QuoteLineEdit {
  id?: string;
  itemName?: string;
  supplierName?: string;
  description?: string;
  qty: number;
  unitPrice: number;
  unitCost: number;
}
export function useUpdateQuoteLines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lines }: { id: string; lines: QuoteLineEdit[] }) => api.patch<Quote>(`/api/quotes/${id}`, { lines }),
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.setQueryData(['quote', q.id], q);
    },
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/quotes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useDeleteItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  });
}

export function useCustomers() {
  return useQuery({ queryKey: ['customers'], queryFn: () => api.get<MappedEntity[]>('/api/customers') });
}
export function useItems() {
  return useQuery({ queryKey: ['items'], queryFn: () => api.get<MappedEntity[]>('/api/items') });
}
export function useSuppliers() {
  return useQuery({ queryKey: ['suppliers'], queryFn: () => api.get<MappedEntity[]>('/api/suppliers') });
}
export function useIncomeAccounts(enabled = true) {
  return useQuery({
    queryKey: ['income-accounts'],
    queryFn: () => api.get<IncomeAccount[]>('/api/items/income-accounts'),
    enabled,
    retry: false,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post('/api/customers', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}
export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post('/api/items', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  });
}
export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post('/api/suppliers', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

// Create several items in one call.
export function useBatchItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: ItemInput[]) => api.post<{ results: ResolveResult[] }>('/api/items/batch', { items }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  });
}

// Commit a reviewed orchestration plan (customer / items / quote).
export function useCommitPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plan: CommitPlanInput) => api.post<CommitResult>('/api/orchestrate/commit', plan),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// QBO reference data for dropdowns.
export function useRef(kind: string) {
  return useQuery({ queryKey: ['ref', kind], queryFn: () => api.get<RefItem[]>(`/api/reference/${kind}`), staleTime: 300_000 });
}
export function useAccounts(type?: string) {
  const q = type ? `?type=${encodeURIComponent(type)}` : '';
  return useQuery({
    queryKey: ['ref', 'accounts', type ?? 'all'],
    queryFn: () => api.get<AccountRef[]>(`/api/reference/accounts${q}`),
    staleTime: 300_000,
  });
}

// ---- Admin ----
export function useUsers(enabled = true) {
  return useQuery({ queryKey: ['users'], queryFn: () => api.get<AppUser[]>('/api/admin/users'), enabled });
}
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { upn: string; displayName: string; email?: string; role: Role; isActive?: boolean }) =>
      api.post<AppUser>('/api/admin/users', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Pick<AppUser, 'displayName' | 'email' | 'role' | 'isActive'>> }) =>
      api.patch<AppUser>(`/api/admin/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
export function useAudit(enabled = true) {
  return useQuery({ queryKey: ['audit'], queryFn: () => api.get<AuditRecord[]>('/api/admin/audit'), enabled });
}
